import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import LoadContentSpinner from '../../../components/Utils/LoadContentSpinner';
import { formatPrice } from '../../../components/Utils/UtilsPricesFormat';
import FormattedDate from '../../../components/Utils/FormattedDate';
import PaymentLinkStatusBadge from './PaymentLinkStatusBadge';
import ModalEditPaymentLinkRecipient from './ModalEditPaymentLinkRecipient';
import { cancelPaymentLink, fetchPaymentLinkById } from './paymentLinksStore';

export default function PaymentLinkDetail() {
    const { id } = useParams();
    const [copied, setCopied] = useState(false);
    const [link, setLink] = useState(null);
    const [loading, setLoading] = useState(true);
    const [cancelling, setCancelling] = useState(false);
    const [editRecipientOpen, setEditRecipientOpen] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const showSuccess = (message) => {
        setSuccessMessage(message);
        setTimeout(() => setSuccessMessage(''), 5000);
    };

    useEffect(() => {
        document.title = 'Détail lien de paiement - Events';
        let cancelled = false;
        setLoading(true);
        setError('');
        fetchPaymentLinkById(id)
            .then((data) => {
                if (cancelled) return;
                setLink(data);
            })
            .catch((err) => {
                if (cancelled) return;
                setLink(null);
                setError(
                    err?.response?.status === 404
                        ? 'Ce lien n’existe pas ou a été supprimé.'
                        : 'Impossible de charger ce lien.',
                );
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [id]);

    if (loading) {
        return <LoadContentSpinner />;
    }

    if (!link) {
        return (
            <div className="ch3yp cnbwt cs7xl clu2m c6btv clbq0 cxsfz">
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-8 text-center">
                    <h1 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-2">
                        Lien introuvable
                    </h1>
                    <p className="text-sm text-gray-500 mb-4">
                        {error || 'Ce lien n’existe plus.'}
                    </p>
                    <Link
                        to="/dashboard/communiquer/liens-paiement"
                        className="text-sm font-semibold text-primary-700 hover:underline"
                    >
                        Retour à la liste
                    </Link>
                </div>
            </div>
        );
    }

    const amount =
        Number(link.total_amount) ||
        (Number(link.unit_price) || 0) * (Number(link.quantity) || 0);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(link.public_url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        } catch {
            // ignore
        }
    };

    const handleCancel = async () => {
        if (!window.confirm('Annuler ce lien ? Il ne pourra plus être utilisé pour payer.')) return;
        setCancelling(true);
        try {
            const updated = await cancelPaymentLink(link.id);
            setLink(updated);
            showSuccess('Lien de paiement annulé.');
        } catch (err) {
            window.alert(err?.response?.data?.message || 'Impossible d’annuler ce lien.');
        } finally {
            setCancelling(false);
        }
    };

    const waShareUrl = `https://wa.me/?text=${encodeURIComponent(
        `${link.recipient_name ? `Bonjour ${link.recipient_name},\n\n` : ''}Voici votre lien de paiement pour ${link.event_title} (${
            link.pricing_mode === 'custom'
                ? link.ticket_title || 'Montant libre'
                : `${link.ticket_title} × ${link.quantity}`
        }) :\n${link.public_url}`,
    )}`;

    return (
        <div className="ch3yp cnbwt cs7xl clu2m c6btv clbq0 cxsfz max-w-3xl mx-auto">
            {successMessage && (
                <div className="flex w-full justify-center items-center relative">
                    <div className="text-sm fixed mt-10 z-[200] px-3 rounded-lg bg-green-100 text-gray-700 shadow-md">
                        <div className="flex items-center gap-2 py-2">
                            <svg className="text-green-500 shrink-0" width={16} height={16} viewBox="0 0 16 16" fill="currentColor">
                                <path d="M8 0C3.6 0 0 3.6 0 8s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zM7 11.4L3.6 8 5 6.6l2 2 4-4L12.4 6 7 11.4z" />
                            </svg>
                            <div>{successMessage}</div>
                        </div>
                    </div>
                </div>
            )}

            <Link
                to="/dashboard/communiquer/liens-paiement"
                className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary-700 dark:hover:text-primary-300 mb-4"
            >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
                Liens de paiement
            </Link>

            <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
                <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                        <PaymentLinkStatusBadge status={link.status} />
                        <span className="text-xs text-gray-400 font-mono">#{link.token}</span>
                    </div>
                    <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 tracking-tight">
                        {link.recipient_name || 'Lien sans destinataire'}
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">
                        {link.event_title}
                        {' · '}
                        {link.pricing_mode === 'custom'
                            ? `${link.ticket_title || 'Montant libre'}`
                            : `${link.ticket_title} × ${link.quantity}`}
                    </p>
                </div>
                <p className="text-xl font-bold tabular-nums text-primary-700 dark:text-primary-300">
                    {formatPrice(amount)}
                </p>
            </div>

            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 mb-4">
                <p className="text-[11px] uppercase tracking-wider font-semibold text-gray-400 mb-2">
                    Lien public
                </p>
                <p className="text-sm break-all text-gray-800 dark:text-gray-100 mb-4 font-medium">
                    {link.public_url}
                </p>
                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={handleCopy}
                        className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700"
                    >
                        {copied ? 'Copié' : 'Copier'}
                    </button>
                    <a
                        href={waShareUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-semibold border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                        WhatsApp
                    </a>
                    {link.status === 'active' && (
                        <button
                            type="button"
                            onClick={handleCancel}
                            disabled={cancelling}
                            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-semibold border border-rose-200 text-rose-700 hover:bg-rose-50 dark:border-rose-500/40 dark:text-rose-300 dark:hover:bg-rose-500/10 disabled:opacity-60"
                        >
                            {cancelling ? 'Annulation…' : 'Annuler le lien'}
                        </button>
                    )}
                </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 space-y-3 text-sm">
                    <div className="flex items-center justify-between gap-2">
                        <h2 className="font-bold text-gray-800 dark:text-gray-100">Destinataire</h2>
                        {link.status !== 'cancelled' && (
                            <button
                                type="button"
                                onClick={() => setEditRecipientOpen(true)}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 dark:border-gray-600 text-gray-500 hover:text-primary-700 hover:border-primary-300 hover:bg-primary-50 dark:hover:bg-primary-500/10 dark:hover:text-primary-300 transition-colors"
                                title="Modifier le destinataire"
                                aria-label="Modifier le destinataire"
                            >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                </svg>
                            </button>
                        )}
                    </div>
                    <div>
                        <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Nom</p>
                        <p className="text-gray-800 dark:text-gray-100">{link.recipient_name || '—'}</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Téléphone</p>
                        <p className="text-gray-800 dark:text-gray-100">{link.recipient_phone || '—'}</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">E-mail</p>
                        <p className="text-gray-800 dark:text-gray-100 break-all">{link.recipient_email || '—'}</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Note</p>
                        <p className="text-gray-800 dark:text-gray-100 whitespace-pre-wrap">{link.note || '—'}</p>
                    </div>
                </div>

                <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 space-y-3 text-sm">
                    <h2 className="font-bold text-gray-800 dark:text-gray-100">Contrôle</h2>
                    <div>
                        <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Créé le</p>
                        <p className="text-gray-800 dark:text-gray-100">
                            <FormattedDate date={link.created_at} />
                        </p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Expire le</p>
                        <p className="text-gray-800 dark:text-gray-100">
                            <FormattedDate date={link.expires_at} />
                        </p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Usages</p>
                        <p className="text-gray-800 dark:text-gray-100">
                            {link.uses_count || 0} / {link.max_uses || 1}
                        </p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Ouvertures</p>
                        <p className="text-gray-800 dark:text-gray-100">{link.opens_count || 0}</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Prix unitaire</p>
                        <p className="text-gray-800 dark:text-gray-100">{formatPrice(link.unit_price)}</p>
                    </div>
                </div>
            </div>

            {link.status !== 'cancelled' && (
                <ModalEditPaymentLinkRecipient
                    isOpen={editRecipientOpen}
                    onClose={() => setEditRecipientOpen(false)}
                    link={link}
                    onSuccess={(updated, message) => {
                        setLink(updated);
                        showSuccess(message || 'Destinataire mis à jour.');
                    }}
                />
            )}
        </div>
    );
}
